<script lang="ts">
  import { useQuery, type QueryResponseInitial } from '@sanity/svelte-loader';
  import { TwistGrid, TwistGridItem, ResponsiveVars } from '@twist/core';
  import type { Footer } from '$lib/sanity/schemas';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { GetFooterQuery } from '$lib/sanity/queries/common';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import Logo from '$components/Logos/Logo.svelte';
  import FooterLinks from '$components/News/Footer/FooterLinks.svelte';
  import { BGMode } from '$components/News/Background/BackgroundGradient.svelte';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';
  import { cx } from '@emotion/css';

  export let document: QueryResponseInitial<Footer>;

  const brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);

  const currentYear = new Date().getFullYear().toString();

  const query = useQuery<Footer>(GetFooterQuery, undefined, {
    initial: document
  });

  $: ({ data: footer, sourceMap } = $query);

  const responsiveVars = new ResponsiveVars()
    .addFluid('logo-width', toFluidVarInput(140, 210))
    .addFluid('logo-padding', [
      ['min', 36],
      ['sm', 'lg', 36, 72],
      ['lg', 'max', 24, 38],
      ['max', 38]
    ])
    .addFluid('footer-padding', toFluidVarInput(0, 102))
    .toCSS();
</script>

{#if footer}
  <footer class={responsiveVars} data-background data-bgmode={BGMode.INITIAL} data-testid="footer">
    <div class="footer-container">
      <div class="grid-container">
        <TwistGrid>
          <TwistGridItem colSpan={{ min: 4, lg: 3 }} rowStart={1}>
            <div class="logo-container">
              <div class={cx('logo', { 'logo-reskin': brandReskinEnabled })}>
                <Logo size={brandReskinEnabled ? '40' : '200'} />
              </div>
            </div>
          </TwistGridItem>
          <TwistGridItem
            colSpan={{ min: 4, lg: 3 }}
            colStart={{ min: 1, lg: 4 }}
            rowStart={{ min: 2, lg: 1 }}
          >
            <div class="company-links" data-testid="company-links">
              <FooterLinks
                linksTitle={footer.companyLinksTitle}
                links={footer.companyLinks}
                titlePath={['companyLinksTitle']}
                linksPath={['companyLinks']}
                {sourceMap}
              />
            </div>
          </TwistGridItem>
          <TwistGridItem
            colSpan={{ min: 4, lg: 3 }}
            colStart={{ min: 1, lg: 7 }}
            rowStart={{ min: 3, lg: 1 }}
          >
            <div class="bu-links" data-testid="bu-links">
              <FooterLinks
                linksTitle={footer.buLinksTitle}
                links={footer.buLinks}
                titlePath={['buLinksTitle']}
                linksPath={['buLinks']}
                {sourceMap}
              />
            </div>
          </TwistGridItem>
          <TwistGridItem
            colSpan={{ min: 4, lg: 3 }}
            colStart={{ min: 1, lg: 10 }}
            rowStart={{ min: 4, lg: 1 }}
          >
            <div class="social-links" data-testid="social-links">
              <FooterLinks
                linksTitle={footer.socialLinksTitle}
                links={footer.socialLinks}
                titlePath={['socialLinksTitle']}
                linksPath={['socialLinks']}
                {sourceMap}
              />
            </div>
          </TwistGridItem>
          <TwistGridItem
            colSpan={{ min: 4, lg: 8 }}
            colStart={{ min: 1, lg: 4 }}
            rowStart={{ min: 5, lg: 2 }}
          >
            {#if brandReskinEnabled}
              <FontSizeDecorator size="caption">
                {`© ${currentYear}  Block, Inc. BLOCK and the Block Logo are trademarks of Block, Inc.`}
              </FontSizeDecorator>
            {:else}
              <div class="trademark">
                {`© ${currentYear}  Block, Inc. BLOCK and the Block Logo are trademarks of Block, Inc.`}
              </div>
            {/if}
          </TwistGridItem>
        </TwistGrid>
      </div>
    </div>
  </footer>
{/if}

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';

  footer {
    width: 100%;
    max-width: $max-width-page;
    padding: var(--twist-core-spacing-0) var(--twist-core-spacing-4);
    margin: var(--twist-core-spacing-0) auto;
    box-sizing: border-box;

    @include at('lg') {
      padding: var(--twist-core-spacing-0) var(--footer-padding);
    }
  }

  .footer-container {
    padding: 0 0 100px;
    @include at('lg') {
      padding: 60px 0 100px;
    }
  }

  .grid-container {
    @include at('lg') {
      padding-top: 60px;
    }
  }

  .logo-container {
    display: flex;
    justify-content: center;
    margin: 60px 0;
    padding: 0 var(--logo-padding);

    @include at('lg') {
      justify-content: flex-start;
      margin: 0;
      max-width: unset;
    }

    .logo {
      width: var(--logo-width);

      &.logo-reskin {
        width: auto;
      }
    }
  }

  .company-links {
    @include at('lg') {
      margin-left: 24px;
    }
  }

  .trademark {
    display: flex;
    justify-content: center;
    text-align: center;
    font-family: var(--font-family-primary);
    font-size: 12px;
    line-height: 15px;

    @include at('lg') {
      justify-content: flex-start;
      text-align: start;
      margin-left: 24px;
    }
  }
</style>
